import { type Node, type Edge } from '@xyflow/react';
import React, {
  useState,
  useCallback,
  type ReactNode,
  useEffect,
  useLayoutEffect,
} from 'react';
import { useLocalStorage } from 'react-use';
import { v4 as uuid } from 'uuid';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { LocalStorageKey } from 'src/core/constants/storage';

import { WorkflowContext } from './WorkflowContext';
import {
  getNodeLevel,
  addHelperBranches,
  calculateSiblingNodes,
  findDescendantNodes,
  calculateChildNodes,
  calculateParentNode,
  moveNewConditionNodesAtTheStart,
} from './helpers';
import { useCompanyId } from '../../app/hooks/useCompanyId';
import { useDesignatedCustomFieldQueryPromise } from '../../custom-fields/hooks/useDesignatedCustomFieldQuery';
import { editApprovalWorkflow } from '../api/editApprovalWorkflow';
import { convertApprovalWorkflowToApi } from '../helpers/convertApprovalWorkflowToApi';
import { makeTreeLayout } from '../helpers/makeTreeLayout';
import { transformNodesAndEdgesToApprovalWorkflow } from '../helpers/transformNodesAndEdgesToApprovalWorkflow';
import { useDimensionValues } from '../hooks/useDimensionValues';
import { type CustomNode } from '../node';

export const WorkflowProvider = ({ children }: { children: ReactNode }) => {
  const [nodes, setNodesState] = useState<CustomNode[]>([]);
  const [edges, setEdgesState] = useState<Edge[]>([]);
  const [workflowView, setWorkflowView] = useLocalStorage<
    'condensed' | 'extended'
  >(LocalStorageKey.ApprovalWorkflowView, 'condensed');
  const [history, setHistory] = useState<
    Array<{ nodes: CustomNode[]; edges: Edge[] }>
  >([]);
  const [redoHistory, setRedoHistory] = useState<
    Array<{ nodes: CustomNode[]; edges: Edge[] }>
  >([]);
  const [nodeSearchQuery, setNodeSearchQuery] = useState<string>('');
  const [displayNodes, setDisplayNodes] = useState<CustomNode[]>([]);
  const [displayEdges, setDisplayEdges] = useState<Edge[]>([]);
  const [selectedNodeId, setSelectedNodeIdState] = useState<string | null>(
    null,
  );
  const [selectedNodeLevel, setSelectedNodeLevel] = useState<number | null>(
    null,
  );
  const [newNodeId, setNewNodeId] = useState<string | null>(null);
  const { t } = useTranslation('global');
  const { getDimensionValues } = useDimensionValues();
  const fetchDesignatedCustomFieldId = useDesignatedCustomFieldQueryPromise();
  const [nodeHeights, setNodeHeights] = useState<Map<string, number>>(
    new Map(),
  );

  const registerNodeHeight = useCallback((nodeId: string, height: number) => {
    if (height > 0) {
      setNodeHeights((previous) => {
        const newMap = new Map(previous);
        const currentHeight = newMap.get(nodeId);
        if (currentHeight !== height) {
          newMap.set(nodeId, height);
          return newMap;
        }
        return previous;
      });
    }
  }, []);

  const getDesignatedCustomFieldId = useCallback(async () => {
    const response = await fetchDesignatedCustomFieldId();
    return response.customFieldId;
  }, [fetchDesignatedCustomFieldId]);

  useEffect(() => {
    if (newNodeId) {
      const level = getNodeLevel(newNodeId, edges);
      setSelectedNodeLevel(level);
    }
  }, [edges, newNodeId]);

  useLayoutEffect(() => {
    const withHelperBranches = addHelperBranches({
      nodes,
      edges,
      t,
      getDimensionValues,
    });
    const sortedNodes = moveNewConditionNodesAtTheStart(
      withHelperBranches.nodes,
    );
    const { displayNodes: newDisplayNodes, displayEdges: newDisplayEdges } =
      makeTreeLayout(sortedNodes, withHelperBranches.edges, nodeHeights);

    setDisplayNodes(newDisplayNodes);
    setDisplayEdges(newDisplayEdges);
  }, [nodes, edges, t, nodeHeights]);

  const setNodes = useCallback((ns: Node[]) => {
    setNodesState(ns as CustomNode[]);
  }, []);

  const setEdges = useCallback((es: Edge[]) => {
    setEdgesState(es);
  }, []);

  const pushHistorySnapshot = useCallback(() => {
    setHistory((previous) => [
      ...previous,
      {
        nodes: nodes.map((node) => ({ ...node })),
        edges: edges.map((edge) => ({ ...edge })),
      },
    ]);
    // Clear redo history when a new action is performed
    setRedoHistory([]);
  }, [nodes, edges, nodeHeights]);

  const undo = useCallback(() => {
    setHistory((previous) => {
      if (previous.length === 0) {
        return previous;
      }
      const snapshot = previous.at(-1);
      if (!snapshot) {
        return previous;
      }
      // Save current state to redo history before undoing
      setRedoHistory((redoPrevious) => [
        ...redoPrevious,
        {
          nodes: nodes.map((node) => ({ ...node })),
          edges: edges.map((edge) => ({ ...edge })),
        },
      ]);
      setNodesState(snapshot.nodes);
      setEdgesState(snapshot.edges);
      setSelectedNodeId(null);

      // we need to submit the approval flow again to update the backend
      handleSubmitApprovalFlow(snapshot.nodes, snapshot.edges);
      return previous.slice(0, -1);
    });
  }, [nodes, edges]);

  const redo = useCallback(() => {
    setRedoHistory((redoPrevious) => {
      if (redoPrevious.length === 0) {
        return redoPrevious;
      }
      const snapshot = redoPrevious.at(-1);
      if (!snapshot) {
        return redoPrevious;
      }
      setHistory((previous) => [
        ...previous,
        {
          nodes: nodes.map((node) => ({ ...node })),
          edges: edges.map((edge) => ({ ...edge })),
        },
      ]);
      setNodesState(snapshot.nodes);
      setEdgesState(snapshot.edges);
      setSelectedNodeId(null);

      handleSubmitApprovalFlow(snapshot.nodes, snapshot.edges);
      return redoPrevious.slice(0, -1);
    });
  }, [nodes, edges]);

  const setSelectedNodeId = useCallback(
    (nodeId: string | null, clearSearch: boolean = false) => {
      setSelectedNodeIdState(nodeId);
      if (nodeId) {
        const level = getNodeLevel(nodeId, edges);
        setSelectedNodeLevel(level);
        if (clearSearch) {
          setNodeSearchQuery('');
        }
      }
      if (nodeId === null) {
        setNewNodeId(null);
        setNodeSearchQuery('');
      }
    },
    [edges],
  );

  const createAndConnectNode = useCallback(
    ({ sourceNodeId }: { sourceNodeId: string }) => {
      pushHistorySnapshot();
      const id = `node-${uuid()}`;
      setNewNodeId(id);

      const newNode: CustomNode = {
        id,
        type: 'baseNode',
        data: {
          schemeId: undefined,
          dimension: undefined,
          values: undefined,
        },
        position: { x: 0, y: 0 },
      };

      setNodesState((nds) => [newNode, ...nds]);
      setEdgesState((es) => [
        ...es,
        {
          id: `edge-${uuid()}`,
          source: sourceNodeId,
          target: newNode.id,
          type: 'labelEdge',
          data: {
            labelText: t('approvalWorkflows.labelEdge.if'),
            labelVariant: 'purple',
          },
        },
      ]);

      // We set the new node as selected because it's the last node added
      // The panel will open automatically
      setSelectedNodeId(id);
    },
    [pushHistorySnapshot],
  );

  const getSiblingNodes = useCallback(
    (nodeId: string) => {
      return calculateSiblingNodes(nodeId, displayNodes, edges);
    },
    [edges, displayNodes],
  );

  const getParentNode = useCallback(
    (nodeId: string) => {
      return calculateParentNode(nodeId, nodes, edges);
    },
    [edges, nodes],
  );

  const getAllParentNodes = useCallback(
    (nodeId: string): CustomNode[] => {
      const parentNode = getParentNode(nodeId);
      if (!parentNode || parentNode.type !== 'baseNode') {
        return [];
      }
      return [parentNode, ...getAllParentNodes(parentNode.id)];
    },
    [getParentNode],
  );

  const getChildrenNodes = useCallback(
    (nodeId: string): CustomNode[] => {
      return calculateChildNodes(nodeId, nodes, edges);
    },
    [nodes, edges],
  );

  const isLeafNode = useCallback(
    (nodeId: string) => {
      const childrenNodes = getChildrenNodes(nodeId);
      return childrenNodes.length === 0;
    },
    [getChildrenNodes],
  );

  const deleteNode = useCallback(
    (nodeId: string) => {
      pushHistorySnapshot();
      const descendantNodeIds = findDescendantNodes(nodeId, nodes, edges);
      const newNodes = nodes.filter(
        (node) => !descendantNodeIds.includes(node.id),
      );
      const newEdges = edges.filter(
        (edge) =>
          !descendantNodeIds.includes(edge.source) &&
          !descendantNodeIds.includes(edge.target),
      );

      setNodesState(newNodes);
      setEdgesState(newEdges);
      handleSubmitApprovalFlow(newNodes, newEdges);
    },
    [nodes, edges, pushHistorySnapshot],
  );

  const updateNode = useCallback(
    (id: string, newData: Partial<CustomNode['data']>) => {
      pushHistorySnapshot();
      setNodesState((previousNodes) => {
        const updatedNodes = previousNodes.map((node) =>
          node.id === id && node.type === 'baseNode'
            ? { ...node, data: { ...node.data, ...newData } }
            : node,
        );

        setNodesState(updatedNodes);
        setEdgesState(edges);
        return updatedNodes;
      });
    },
    [pushHistorySnapshot, edges],
  );

  const companyId = useCompanyId();
  const handleSubmitApprovalFlow = async (
    updatedNodes: CustomNode[],
    updatedEdges: Edge[],
  ) => {
    const hasExpenseCategory = updatedNodes.some(
      (node) =>
        node.type === 'baseNode' &&
        node.data.dimension?.type === 'expenseCategory',
    );

    let designatedCustomFieldId: string | undefined;
    let nodesReadyForSubmit = updatedNodes;

    if (hasExpenseCategory) {
      designatedCustomFieldId = await getDesignatedCustomFieldId();
      nodesReadyForSubmit = getNodesWithExpenseCategoriesConverted(
        updatedNodes,
        designatedCustomFieldId,
      );
    }

    const approvalWorkflow = transformNodesAndEdgesToApprovalWorkflow(
      nodesReadyForSubmit,
      updatedEdges,
    );
    const apiApprovalWorkflow = convertApprovalWorkflowToApi(
      approvalWorkflow,
      designatedCustomFieldId,
    );

    try {
      await editApprovalWorkflow({
        companyId,
        approvalWorkflow: apiApprovalWorkflow,
      });
    } catch (error) {
      // Revert the changes in the UI state
      setNodesState(nodes);
      setEdgesState(edges);
      throw error;
    }
  };

  return (
    <WorkflowContext.Provider
      value={{
        nodes: displayNodes,
        edges: displayEdges,
        selectedNodeId,
        newNodeId,
        selectedNodeLevel,
        setNodeSearchQuery,
        nodeSearchQuery,
        setNodes,
        setEdges,
        updateNode,
        setSelectedNodeId,
        deleteNode,
        createAndConnectNode,
        getSiblingNodes,
        getParentNode,
        getChildrenNodes,
        getAllParentNodes,
        handleSubmitApprovalFlow,
        isLeafNode,
        undo,
        canUndo: history.length > 0,
        redo,
        canRedo: redoHistory.length > 0,
        registerNodeHeight,
        workflowView: workflowView ?? 'condensed',
        setWorkflowView,
      }}
    >
      {children}
    </WorkflowContext.Provider>
  );
};

// This function is used to convert the expense categories to custom fields as expense category is treated as a custom field on the backend
const getNodesWithExpenseCategoriesConverted = (
  nodes: CustomNode[],
  designatedCustomFieldId: string,
): CustomNode[] => {
  return nodes.map((node) => {
    if (
      node.type === 'baseNode' &&
      node.data.dimension?.type === 'expenseCategory'
    ) {
      return {
        ...node,
        data: {
          ...node.data,
          dimension: {
            type: 'customField',
            customFieldId: designatedCustomFieldId,
          },
        },
      };
    }
    return node;
  });
};
